/*
 * Vesper floating widgets
 * - <scroll-to-top>: appears after scrolling data-offset px, shows scroll progress in its ring
 * - <music-player>: play/pause background audio; the "global" player resumes across page loads
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ScrollToTop extends HTMLElement {
    connectedCallback() {
      this.offset = Number(this.dataset.offset) || 0;
      this.button = this.querySelector('button');
      this.ring = this.querySelector('.vscrolltop__ring circle');
      this.removeAttribute('hidden');
      this.button.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        const target = document.getElementById('MainContent');
        if (target) target.focus({ preventScroll: true });
      });
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    update() {
      const y = window.scrollY;
      const visible = y > this.offset && y > 0;
      this.classList.toggle('is-visible', visible);
      this.button.tabIndex = visible ? 0 : -1;
      this.button.setAttribute('aria-hidden', visible ? 'false' : 'true');
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, y / max) : 0;
      if (this.ring) this.ring.style.strokeDashoffset = String(100 - progress * 100);
    }
  }
  if (!customElements.get('scroll-to-top')) customElements.define('scroll-to-top', ScrollToTop);

  class MusicPlayer extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('audio');
      this.button = this.querySelector('button');
      if (!this.audio || !this.button) return;
      this.key = `vesper-music-${this.dataset.key || 'page'}`;
      this.isGlobal = this.dataset.key === 'global';

      // A page-level music player takes priority over the global one.
      if (this.isGlobal && document.querySelector('music-player:not([data-key="global"])')) {
        this.hidden = true;
        return;
      }

      this.audio.volume = Math.max(0, Math.min(1, Number(this.dataset.volume) || 0));
      this.button.addEventListener('click', () => (this.audio.paused ? this.play() : this.pause()));
      this.audio.addEventListener('play', () => this.render(true));
      this.audio.addEventListener('pause', () => this.render(false));

      if (this.isGlobal) {
        window.addEventListener('pagehide', () => this.save());
        this.resume();
      }
    }

    state() {
      try {
        return JSON.parse(sessionStorage.getItem(this.key) || '{}');
      } catch (e) {
        return {};
      }
    }

    save() {
      try {
        sessionStorage.setItem(this.key, JSON.stringify({ playing: !this.audio.paused, time: this.audio.currentTime }));
      } catch (e) {}
    }

    resume() {
      const { playing, time } = this.state();
      if (!playing) return;
      this.audio.preload = 'auto';
      const start = () => {
        if (time) {
          try {
            this.audio.currentTime = time;
          } catch (e) {}
        }
        this.play(true);
      };
      if (this.audio.readyState >= 1) start();
      else this.audio.addEventListener('loadedmetadata', start, { once: true });
      this.audio.load();
    }

    play(silentFail = false) {
      const attempt = this.audio.play();
      if (attempt && attempt.catch) {
        attempt.catch(() => {
          // Autoplay blocked by the browser: start on the visitor's next interaction.
          this.render(false);
          if (!silentFail) return;
          const once = () => {
            this.audio.play().catch(() => {});
            ['pointerdown', 'keydown'].forEach((t) => document.removeEventListener(t, once, true));
          };
          ['pointerdown', 'keydown'].forEach((t) => document.addEventListener(t, once, true));
        });
      }
    }

    pause() {
      this.audio.pause();
      this.save();
    }

    render(playing) {
      this.classList.toggle('is-playing', playing);
      this.button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      this.button.setAttribute('aria-label', playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay);
      if (this.isGlobal) this.save();
    }
  }
  if (!customElements.get('music-player')) customElements.define('music-player', MusicPlayer);
})();
